import { useEffect, useState } from "react";
import { Phone, MapPin, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/logo.svg";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-primary/95 backdrop-blur-lg shadow-xl shadow-primary-dark/20 py-2"
          : "bg-linear-to-b from-black/70 via-black/30 to-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#hero"
          className="flex items-center gap-3 group"
          aria-label="El Verde – inicio"
        >
          <div
            className={`rounded-full flex items-center justify-center transition-all duration-300 ${
              scrolled
                ? "w-10 h-10 bg-accent shadow-md"
                : "w-12 h-12 bg-accent/90 shadow-lg shadow-accent/25"
            }`}
          >
            <img
              className={`transition-all duration-300 ${scrolled ? "w-7 h-7" : "w-8 h-8"}`}
              src={logo}
              alt=""
            />
          </div>
          <div className="flex flex-col">
            <span className="font-heading text-xl sm:text-2xl font-bold text-white tracking-tight leading-none">
              El Verde
            </span>
            <span
              className={`text-[10px] font-semibold tracking-[0.2em] uppercase transition-all overflow-hidden ${
                scrolled
                  ? "max-h-0 opacity-0"
                  : "max-h-5 opacity-70 text-white/70 mt-0.5"
              }`}
            >
              Comida Costeña
            </span>
          </div>
        </a>

        {/* Desktop nav and info */}
        <div className="hidden md:flex items-center gap-6 text-white/90 text-sm">
          <a
            href="/contact"
            className="px-6 py-2 rounded-full bg-accent/15 hover:bg-accent hover:text-primary-dark transition-colors font-medium whitespace-nowrap"
          >
            Nuestros Enlaces
          </a>
          <div className="w-px h-8 bg-white/10"></div>
          <div className="flex flex-col gap-1.5">
            <a
              href="tel:+593983423290"
              className="flex items-center gap-2 hover:text-accent transition-colors bg-white/10 rounded-full px-4 py-2 w-fit"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="font-medium">+593 98 342 3290</span>
            </a>
            <div className="flex items-center gap-2 text-white/60 px-4">
              <MapPin className="w-3.5 h-3.5 text-accent flex-shrink-0" />
              <span className="text-xs">Ordóñez Lasso y Arrayanes</span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <a
              href="tel:+593983423222"
              className="flex items-center gap-2 hover:text-accent transition-colors bg-white/10 rounded-full px-4 py-2 w-fit"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="font-medium">+593 98 342 3222</span>
            </a>
            <div className="flex items-center gap-2 text-white/60 px-4">
              <MapPin className="w-3.5 h-3.5 text-accent flex-shrink-0" />
              <span className="text-xs">10 de Agosto y Sangurima</span>
            </div>
          </div>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-11 h-11 flex items-center justify-center text-white rounded-xl hover:bg-white/10 active:bg-white/20 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={mobileOpen}
        >
          <AnimatePresence mode="wait" initial={false}>
            {mobileOpen ? (
              <motion.span
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <X className="w-5 h-5" />
              </motion.span>
            ) : (
              <motion.span
                key="menu"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <Menu className="w-5 h-5" />
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="md:hidden overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="px-6 py-4 space-y-4 border-t border-white/10 mt-2"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } },
              }}
            >
              <motion.a
                href="/contact"
                className="flex items-center justify-center w-full px-4 py-3 rounded-full bg-accent/15 hover:bg-accent hover:text-primary-dark transition-colors font-medium text-white/90 text-sm"
                variants={{
                  hidden: { opacity: 0, y: -8 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                Nuestros Enlaces
              </motion.a>
              <motion.div
                className="space-y-1.5"
                variants={{
                  hidden: { opacity: 0, y: -8 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <a
                  href="tel:+593983423290"
                  className="flex items-center gap-2 text-white/90 text-sm bg-white/10 rounded-full px-3 py-2 w-fit"
                >
                  <Phone className="w-4 h-4 text-accent" />
                  +593 98 342 3290
                </a>
                <div className="flex items-center gap-2 text-white/60 text-xs px-3">
                  <MapPin className="w-4 h-4 text-accent flex-shrink-0" />
                  Ordóñez Lasso y Arrayanes
                </div>
              </motion.div>
              <motion.div
                className="space-y-1.5"
                variants={{
                  hidden: { opacity: 0, y: -8 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <a
                  href="tel:+593983423222"
                  className="flex items-center gap-2 text-white/90 text-sm bg-white/10 rounded-full px-3 py-2 w-fit"
                >
                  <Phone className="w-4 h-4 text-accent" />
                  +593 98 342 3222
                </a>
                <div className="flex items-center gap-2 text-white/60 text-xs px-3">
                  <MapPin className="w-4 h-4 text-accent flex-shrink-0" />
                  10 de Agosto y Sangurima
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
